import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trophy, Calendar, MapPin, Users, CalendarDays, Archive } from 'lucide-react';
import { CompetitionLifecycleAction } from '../components/CompetitionLifecycleControls';
import { AdminModulePage, PageHero } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { setupStatus, getTournamentType } from '../services/tournamentService';
import { paths, tournamentSlug } from '../lib/paths';
import type { CompetitionFormat } from '../models';

interface ListPageProps {
  /** Filter the list by format. Defaults to 'tournament'. */
  formatFilter?: CompetitionFormat;
}

export function TournamentsListPage({ formatFilter = 'tournament' }: ListPageProps = {}) {
  const db = useStore((d) => d);
  const [showArchived, setShowArchived] = useState(false);
  const isSeason = formatFilter === 'season';
  const allForFormat = useMemo(
    () =>
      [...db.tournaments]
        .filter((t) => (t.format ?? 'tournament') === formatFilter)
        .sort((a, b) => b.startsAt.localeCompare(a.startsAt)),
    [db.tournaments, formatFilter],
  );
  const activeCount = allForFormat.filter((t) => t.status !== 'archived').length;
  const archivedCount = allForFormat.filter((t) => t.status === 'archived').length;
  const tournaments = useMemo(
    () => allForFormat.filter((t) => (showArchived ? t.status === 'archived' : t.status !== 'archived')),
    [allForFormat, showArchived],
  );

  const newPath = isSeason ? '/admin/seasons/new' : '/admin/tournaments/new';
  const noun = isSeason ? 'season' : 'tournament';
  const HeroIcon = isSeason ? CalendarDays : Trophy;

  return (
    <AdminModulePage>
      <PageHero
        title={
          isSeason
            ? 'Run a recurring season from week one to playoffs.'
            : 'Run a tighter event from setup to trophy.'
        }
        description={
          isSeason
            ? 'Schedule weekly games, track standings across the year, and seed playoffs from the regular season.'
            : "Drafts live here until you're ready to publish. Publishing never sends a message on its own."
        }
        icon={HeroIcon}
        accent="arc"
      />

      {(activeCount > 0 || archivedCount > 0 || showArchived) && (
        <div className="row row--between" style={{ marginBottom: 16, gap: 12 }}>
          <div className="muted" style={{ fontSize: 13 }}>
            {showArchived
              ? `${archivedCount} archived ${noun}${archivedCount === 1 ? '' : 's'}`
              : `${activeCount} active ${noun}${activeCount === 1 ? '' : 's'}`}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setShowArchived((value) => !value)}
            >
              <Archive size={14} /> {showArchived ? `Active ${noun}s` : 'Archived'}
            </button>
            {!showArchived && (
              <Link to={newPath} className="btn btn--primary btn--sm">
                <Plus size={14} /> New {noun}
              </Link>
            )}
          </div>
        </div>
      )}

      {tournaments.length === 0 ? (
        <div className="empty">
          <div className="empty__icon">
            <HeroIcon size={18} />
          </div>
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>
            {showArchived ? `No archived ${noun}s` : `No ${noun}s yet`}
          </div>
          <p style={{ marginTop: 6 }}>
            {showArchived
              ? `Archived ${noun}s will appear here after they are removed from active views.`
              : 'Create your first one. It only takes a couple of minutes.'}
          </p>
          {!showArchived && (
            <div className="row" style={{ justifyContent: 'center', marginTop: 14 }}>
              <Link to={newPath} className="btn btn--primary">
                <Plus size={14} /> New {noun}
              </Link>
              {archivedCount > 0 && (
                <button type="button" className="btn btn--ghost" onClick={() => setShowArchived(true)}>
                  <Archive size={14} /> View archived {noun}s
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid--2">
          {tournaments.map((t) => {
            const status = setupStatus(t);
            const type = getTournamentType(t.typeId);
            const venue = db.venues.find((v) => v.id === t.primaryVenueId);
            const teamCount = db.teams.filter((tm) => tm.tournamentId === t.id).length;
            const href = paths.adminCompetition(t.format, tournamentSlug(t));
            return (
              <article key={t.id} className="card card--accent competition-card">
                <Link to={href} className="competition-card__hit-area" aria-label={`Open ${t.name}`} />
                <div className="competition-card__content">
                  <div className="row row--between" style={{ alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div className="eyebrow">{type?.label ?? t.typeId}</div>
                      <h3 className="card__title" style={{ marginTop: 4 }}>
                        {t.name}
                      </h3>
                      {t.tagline && (
                        <p className="card__sub" style={{ marginTop: 4 }}>
                          {t.tagline}
                        </p>
                      )}
                    </div>
                    <div className="row competition-card__actions" style={{ gap: 8, flexShrink: 0 }}>
                      <span className={`chip chip--${status.tone === 'ok' ? 'ok' : status.tone === 'warn' ? 'warn' : 'info'}`}>
                        <span className="chip__dot" />
                        {status.label}
                      </span>
                      <span className="competition-card__control">
                        <CompetitionLifecycleAction tournament={t} size="sm" />
                      </span>
                    </div>
                  </div>
                  <div
                    className="row"
                    style={{ marginTop: 14, flexWrap: 'wrap', gap: 14, color: 'var(--text-muted)', fontSize: 13 }}
                  >
                    <span>
                      <Calendar size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                      {formatRange(t.startsAt, t.endsAt)}
                    </span>
                    {venue && (
                      <span>
                        <MapPin size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                        {venue.name}
                      </span>
                    )}
                    <span>
                      <Users size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                      {teamCount} {teamCount === 1 ? 'team' : 'teams'}
                    </span>
                  </div>
                  <div className="row competition-card__open" style={{ marginTop: 18 }}>
                    <Link to={href} className="btn btn--ghost btn--sm competition-card__control">
                      Open
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </AdminModulePage>
  );
}

function formatRange(a: string, b: string): string {
  if (!a) return '-';
  const sa = new Date(a);
  const sb = b ? new Date(b) : null;
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return sb
    ? `${sa.toLocaleDateString(undefined, opts)} - ${sb.toLocaleDateString(undefined, opts)}, ${sb.getFullYear()}`
    : sa.toLocaleDateString();
}
